import bindData from '@/utilities/bindData'
import {
  apiActivitiesPath,
  readApiActivityPath,
  countApiDocumentsPath,
  showRemarksApiHeadlineCategoriesPath,
  apiCommentLikePath,
  apiArticleCommentsPath,
  apiReportArticleCommentsPath,
  apiCommentPath,
  searchApiCompaniesPath,
  verificationApiPhoneNumbersPath,
  employeeNumApiCompaniesPath,
  lookupApiCompaniesPath,
  followContentsApiMypageIndexPath,
  followStoriesApiMypageIndexPath,
  apiArticlePath,
  apiReportArticlePath,
  followApiStoryPath,
  bulkFollowApiStoriesPath,
  followKeywordsApiMypageIndexPath,
  apiV2RankingIndexPath,
  indexRankingsApiV2KeywordsPath,
  apiPromotionsPath,
  reportsApiV2StoryPath,
  articlesApiV2StoryPath,
  showFollowApiKeywordPath,
  followApiKeywordPath,
} from '@/generated/railsRoutes'

const PROCESS_MESSAGE = {
  REGISTER: '登録',
  ADD     : '追加',
  SAVE    : '保存',
  SETTING : '設定',
  UPDATE  : '更新',
  DELETE  : '削除',
  GET     : '取得',
  POST    : '投稿',
  EDIT    : '編集',
}

/**
 * endpoint サンプル
 * 不要なオプションは適宜削除
 */
export const SAMPLE = {
  /**
   * requestOptions を上書き設定する場合
   *
   * 下の例では、hasConfirm と screenLock の hasLockScreen, hasUnlockScreen の設定は
   * デフォルト指定のままという形に結合されます
   */
  OVERRIDE_REQUEST_OPTIONS: {
    path: '/foo/bar.json',
    method: 'get',
    processMessage: PROCESS_MESSAGE.SETTING,
    ...requestOptions({
      hasToastMessage: true,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasBlackOut: true,
        hasBlur: true,
        hasWaitAnimation: true,
      },
    }),
  },

  /**
   * デフォルトの requestOptions をそのまま使用する場合
   */
  USE_DEFAULT_OPTIONS: {
    path: '/foo/baz.json',
    method: 'post',
    processMessage: '任意の値を入れることも可能',
    ...requestOptions(),
  },
}

/**
 * ヘッダアクティビティ
 */
export const ACTIVITIES = {
  FETCH: {
    path: apiActivitiesPath({ format: 'json' }),
    method: 'get',
    ...requestOptions({
      hasToastMessage: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },

  READ: {
    path: readApiActivityPath,
    method: 'post',
    ...requestOptions({
      hasToastMessage: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: true,
        hasUnlockScreen: true,
      },
    }),
  }
}

/**
 * タイムライン
 */
export const TIMELINE = {
  path: showRemarksApiHeadlineCategoriesPath({ format: 'json' }),
  method: 'get',
  ...requestOptions({
    hasToastMessage: false,
    isClearErrorInfo: {
      globalError: false,
      validationError: false,
    },
    screenLock: {
      hasLockScreen: false,
      hasUnlockScreen: false,
    },
  }),
}

/**
 * コメント
 */
export const COMMENT = {
  // コメントの「いいね」
  LIKE: {
    path: apiCommentLikePath,
    method: 'post',
    ...requestOptions({
      hasToastMessage: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: true,
        hasUnlockScreen: true,
      },
    }),
  },

  EDIT: {
    path: apiCommentPath,
    method: 'put',
    processMessage: PROCESS_MESSAGE.EDIT,
    ...requestOptions({
      hasToastMessage: true,
      isClearErrorInfo: {
        globalError: true,
        validationError: true,
      },
      screenLock: {
        hasLockScreen: true,
        hasUnlockScreen: true,
        hasBlur: true,
        hasBlackOut: false,
        hasWaitAnimation: true,
      },
    }),
  },

  DELETE: {
    path: apiCommentPath,
    method: 'delete',
    processMessage: PROCESS_MESSAGE.DELETE,
    ...requestOptions({
      hasToastMessage: true,
      isClearErrorInfo: {
        globalError: true,
        validationError: true,
      },
      screenLock: {
        hasLockScreen: true,
        hasUnlockScreen: true,
        hasBlur: true,
        hasBlackOut: false,
        hasWaitAnimation: true,
      },
    }),
  },

  REPORT: {
    FETCH: {
      path: apiReportArticleCommentsPath,
      method: 'get',
      ...requestOptions({
        isClearErrorInfo: {
          globalError: false,
          validationError: false,
        },
        screenLock: {
          hasLockScreen: false,
          hasUnlockScreen: false,
        },
      }),
    },

    POST: {
      path: apiReportArticleCommentsPath,
      method: 'post',
      processMessage: PROCESS_MESSAGE.POST,
      ...requestOptions({
        hasToastMessage: true,
        isClearErrorInfo: {
          globalError: true,
          validationError: true,
        },
        screenLock: {
          hasLockScreen: true,
          hasUnlockScreen: true,
        },
      }),
    },
  },

  ARTICLE: {
    FETCH: {
      path: apiArticleCommentsPath,
      method: 'get',
      ...requestOptions({
        isClearErrorInfo: {
          globalError: false,
          validationError: false,
        },
        screenLock: {
          hasLockScreen: false,
          hasUnlockScreen: false,
        },
      }),
    },

    POST: {
      path: apiArticleCommentsPath,
      method: 'post',
      processMessage: PROCESS_MESSAGE.POST,
      ...requestOptions({
        hasToastMessage: true,
        isClearErrorInfo: {
          globalError: true,
          validationError: true,
        },
        screenLock: {
          hasLockScreen: true,
          hasUnlockScreen: true,
        },
      }),
    },
  },
}

/**
 * マイページ
 */
export const MYPAGE = {
  // フォローしているコンテンツ（特集・用語に紐づく記事）を取得
  FETCH_FOLLOWING: {
    path: followContentsApiMypageIndexPath({ format: 'json' }),
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: true,
        hasUnlockScreen: true,
      },
    }),
  },

  // フォローしている特集を取得
  FETCH_FOLLOWING_STORIES: {
    path: followStoriesApiMypageIndexPath({ format: 'json' }),
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },

  // フォローしている用語を取得
  FETCH_FOLLOWING_KEYWORDS: {
    path: followKeywordsApiMypageIndexPath({ format: 'json' }),
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

/**
 * 特集
 */
export const STORIES = {
  // 特定の特集をフォロー
  FOLLOW: {
    path: followApiStoryPath,
    method: 'post',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: true,
        hasUnlockScreen: true,
        hasWaitAnimation: false,
      },
    }),
  },

  // 複数の特集を一括フォロー
  BULK_FOLLOW: {
    path: bulkFollowApiStoriesPath(),
    method: 'post',
    processMessage: PROCESS_MESSAGE.SETTING,
    ...requestOptions({
      hasToastMessage: true,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: true,
        hasUnlockScreen: true,
        hasWaitAnimation: true,
      },
    }),
  },

  FETCH_REPORTS: {
    path: reportsApiV2StoryPath,
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },

  FETCH_ARTICLES: {
    path: articlesApiV2StoryPath,
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

/**
 * 記事
 */
export const REPORT = {
  // ブックマーク
  BOOKMARK: {
    path: apiReportArticlePath,
    method: 'put',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

/**
 * キーワード
 */
export const KEYWORDS = {
  // 特定のキーワードのフォロー状態を取得
  FETCH_FOLLOW: {
    path: showFollowApiKeywordPath,
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
        hasWaitAnimation: false,
      },
    }),
  },

  // 特定のキーワードをフォロー
  FOLLOW: {
    path: followApiKeywordPath,
    method: 'put',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: true,
        hasUnlockScreen: true,
        hasWaitAnimation: false,
      },
    }),
  },
}

/**
 * 外部記事
 */
export const ARTICLE = {
  // ブックマーク
  BOOKMARK: {
    path: apiArticlePath,
    method: 'put',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

/**
 * ランキング
 */
export const RANKING = {
  // 週間ランキング取得
  FETCH_WEEKLY_REPORTS: {
    path: apiV2RankingIndexPath(),
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },

  FETCH_WEEKLY_KEYWORDS: {
    path: indexRankingsApiV2KeywordsPath(),
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

/**
 * 資料
 */
export const DOCUMENTS = {
  // 資料の数をカウント
  COUNT: {
    path: countApiDocumentsPath({ format: 'json' }),
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

/**
 * チャットボット
 */
export const CHATBOT = {
  // Wistia の動画を取得
  CREATE_SESSION: {
    domain: import.meta.env.VITE_ROMANEE_API_DOMAIN,
    path: '/document_chat/documents/:documentId/sessions',
    method: 'post',
    ...requestOptions({
      withCredentials: true,
      hasCsrfCheck: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },

  // チャットメッセージ送信
  SEND_MESSAGE: {
    domain: import.meta.env.VITE_ROMANEE_API_DOMAIN,
    path: '/document_chat/sessions/:sessionId/messages',
    method: 'post',
    ...requestOptions({
      withCredentials: true,
      hasCsrfCheck: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },

  // チャットメッセージ送信（ストリーミング版）
  SEND_MESSAGE_STREAM: {
    domain: import.meta.env.VITE_ROMANEE_API_DOMAIN,
    path: '/document_chat/sessions/:sessionId/messages/stream',
    method: 'post',
    ...requestOptions({
      withCredentials: true,
      hasCsrfCheck: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },

  // チャットメッセージ送信
  FETCH_LOG: {
    domain: import.meta.env.VITE_ROMANEE_API_DOMAIN,
    path: '/document_chat/sessions/:sessionId/conversations',
    method: 'get',
    ...requestOptions({
      withCredentials: true,
      hasCsrfCheck: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

/**
 * 動画
 */
export const VIDEOS = {
  // Wistia の動画を取得
  WISTIA: {
    domain: 'https://fast.wistia.net',
    path: '/oembed',
    method: 'get',
    ...requestOptions({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': '',
      },
      withCredentials: false,
      hasCsrfCheck: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

export const LEAD_GEN = {
  // 電話番号有効性チェック
  VALID_PHONE_NUMBER: {
    path: verificationApiPhoneNumbersPath(),
    method: 'get',
    ...requestOptions({
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      // 実在しない番号は 404 で返るため、失敗は呼び出し側（validator）でステータスを見て判定する
      hasDefaultErrorHandling: false,
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

export const ADDRESS = {
  /**
   * 住所検索
   */
  SEARCH: {
    path: 'https://zipcloud.ibsnet.co.jp/api/search',
    method: 'get',
    processMessage: PROCESS_MESSAGE.GET,
    ...requestOptions({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': '',
      },
      withCredentials: false,
      hasCsrfCheck: false,
      hasToastMessage: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}

export const COMPANY = {
  SEARCH: {
    path: searchApiCompaniesPath({ format: 'json' }),
    method: 'get',
    processMessage: PROCESS_MESSAGE.GET,
    ...requestOptions({
      hasToastMessage: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },

  /**
   * 法人番号（13桁）→ 会社情報（#8796）。未発見・外部 API 失敗は 200 + null、13 桁以外のみ 404
   */
  LOOKUP_BY_CORPORATE_NUMBER: {
    path: lookupApiCompaniesPath({ format: 'json' }),
    method: 'get',
    processMessage: PROCESS_MESSAGE.GET,
    ...requestOptions({
      hasToastMessage: false,
      isClearErrorInfo: {
        globalError: false,
        validationError: false,
      },
      screenLock: {
        hasLockScreen: false,
        hasUnlockScreen: false,
      },
    }),
  },
}


export const EMPLOYEE_NUM = {
  path: employeeNumApiCompaniesPath({ format: 'json' }),
  method: 'get',
  processMessage: PROCESS_MESSAGE.GET,
  ...requestOptions({
    hasToastMessage: false,
    isClearErrorInfo: {
      globalError: false,
      validationError: false,
    },
    screenLock: {
      hasLockScreen: false,
      hasUnlockScreen: false,
    },
  }),
}

export const PROMOTION = {
  CONTENTS: {
    FETCH_BANNERS: {
      path: apiPromotionsPath(),
      method: 'get',
      ...requestOptions({
        isClearErrorInfo: {
          globalError: false,
          validationError: false,
        },
        screenLock: {
          hasLockScreen: false,
          hasUnlockScreen: false,
        },
      }),
    },
  }
}

/**
 * デフォルトオプションの設定を上書きした設定を生成する
 *
 * @param option 上書き設定内容
 */
function requestOptions(option?: ApiRequestOptions) {
  /**
   * api リクエスト処理時の挙動を制御する設定
   */
  const DEFAULT_OPTION = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },

    /**
     * リクエストに CSRF トークンチェックが必要かどうか
     */
    hasCsrfCheck: true,

    /**
     * リクエストの「withCredentials」の設定
     */
    withCredentials: true,

    /**
     * api リクエスト処理正常終了時にトーストメッセージを表示するかどうか
     */
    hasToastMessage: false,

    /**
     * api リクエスト処理開始前に確認モーダルを出すかどうか
     */
    hasConfirm: false,

    /**
     * エラーフォームをクリアするかどうか
     *
     * validationErrorについて
     *  グローバルエラー発生時は false / true に関わらず必ずクリアされることに注意。
     *
     * ※ 現 academy は、現状グローバルエラーが存在していないので（トーストメッセージとしてアラートがでることはあるが、その場合はトーストなので、自動的にフェードアウトしていく）、
     *   実態としてはバリデーションエラーのみのハンドリングとなるため、一応、グローバルエラーのフラグも持たせてはいるものの、将来グローバルエラーが追加された場合の考慮のためで、現状使用はしていない。
     */
    isClearErrorInfo: {
      globalError: true,
      validationError: true,
    },

    /**
     * 失敗時に共通エラーハンドリング（errorStore への反映）を行うかどうか
     * @description false にすると失敗は呼び出し側へ例外として投げ返され、画面のエラー状態は書き換えない
     */
    hasDefaultErrorHandling: true,

    /**
     * スクリーンロック時の挙動制御設定値
     */
    screenLock: {
      /**
       * api リクエスト処理開始後にロックするかどうか
       */
      hasLockScreen: true,

      /**
       * api リクエスト処理完了後にロックを解除するかどうか
       */
      hasUnlockScreen: true,

      /**
       * 背景を暗転させるかどうか
       */
      hasBlackOut: false,

      /**
       * 背景をぼかすかどうか
       */
      hasBlur: false,

      /**
       * 処理中の待機アニメーションを表示するかどうか
       */
      hasWaitAnimation: false,
    },
  }

  const clonedDefaultOption = JSON.parse(JSON.stringify(DEFAULT_OPTION))
  return option ? bindData(clonedDefaultOption, option) : clonedDefaultOption
}
